import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useMemo, useState } from 'react'
import { effectiveWeaponDamage } from '../game/combat'
import {
  computeStashNeedRestorePreview,
  getStashSoftCap,
} from '../game/hideoutProgression'
import {
  CATEGORY_GLYPHS,
  CATEGORY_LABELS,
  ITEMS,
  RARITY_COLOR,
  RARITY_LABELS,
  countByCategory,
} from '../game/items'
import { getPortraitSrc } from '../game/portraits'
import { BOOK_SKILL_MAP, isSkillUnlocked } from '../game/skills'
import { useI18n } from '../i18n/useI18n'
import type { ItemCategory, ItemStack, ItemRarity, Needs } from '../game/types'
import { useGameStore } from '../store/useGameStore'

const MAIN_CATEGORIES: ItemCategory[] = ['food', 'water', 'meds', 'scrap', 'weapon']
const RARITY_ORDER: Record<string, number> = { common: 0, uncommon: 1, special: 2 }
const NEED_KEYS: Record<keyof Needs, 'need_hunger' | 'need_thirst' | 'need_energy'> = {
  hunger: 'need_hunger',
  thirst: 'need_thirst',
  energy: 'need_energy',
}
const USE_ITEM_CATEGORY_CONFIG: Record<
  'food' | 'water' | 'meds',
  { need: keyof Needs; titleKey: string }
> = {
  food: { need: 'hunger', titleKey: 'stash_use_feed_title' },
  water: { need: 'thirst', titleKey: 'stash_use_hydrate_title' },
  meds: { need: 'energy', titleKey: 'stash_use_treat_title' },
}
const USE_ITEM_CATEGORY_NOTE_KEY: Record<'food' | 'water' | 'meds', string> = {
  food: 'stash_use_food_effect_note',
  water: 'stash_use_water_effect_note',
  meds: 'stash_use_meds_effect_note',
}

/** Progress bar: current fill + category-colored delta segment (food brown, water blue, meds red). */
const STASH_DELTA_UI: Record<
  'food' | 'water' | 'meds',
  { barCurrent: string; barDelta: string; textDelta: string }
> = {
  food: {
    barCurrent: 'bg-emerald-800/80',
    barDelta: 'bg-amber-800',
    textDelta: 'text-amber-600',
  },
  water: {
    barCurrent: 'bg-emerald-800/80',
    barDelta: 'bg-sky-500',
    textDelta: 'text-sky-400',
  },
  meds: {
    barCurrent: 'bg-emerald-800/80',
    barDelta: 'bg-red-500',
    textDelta: 'text-red-400',
  },
}

const FILTERS: Array<{ id: 'all' | ItemCategory | 'trinket'; labelKey: string }> = [
  { id: 'all', labelKey: 'stash_filter_all' },
  { id: 'food', labelKey: 'stash_filter_food' },
  { id: 'water', labelKey: 'stash_filter_water' },
  { id: 'meds', labelKey: 'stash_filter_meds' },
  { id: 'scrap', labelKey: 'stash_filter_scrap' },
  { id: 'weapon', labelKey: 'stash_filter_weapons' },
  { id: 'trinket', labelKey: 'stash_filter_trinkets' },
]

export function StashScreen() {
  const { t, pick } = useI18n()
  const inventory = useGameStore((s) => s.inventory)
  const hideoutTierId = useGameStore((s) => s.hideoutTierId)
  const stashLevel = useGameStore((s) => s.workstations.stash.level)
  const trinketPoints = useGameStore((s) => s.trinketPoints)
  const survivors = useGameStore((s) => s.survivors)
  const workstations = useGameStore((s) => s.workstations)
  const stashHasAssignedWorker = workstations.stash.assignedSurvivorId != null
  // const setInventoryLock = useGameStore((s) => s.setInventoryLock)
  const bulkDiscard = useGameStore((s) => s.bulkDiscard)
  const applyItemFromStash = useGameStore((s) => s.useItemFromStash)
  const equipWeaponFromStash = useGameStore((s) => s.equipWeaponFromStash)
  const learnSkillFromBook = useGameStore((s) => s.learnSkillFromBook)

  const [filter, setFilter] = useState<'all' | ItemCategory | 'trinket'>('all')
  const [sortBy, setSortBy] = useState<'rarity' | 'name' | 'quantity'>('rarity')
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<string[]>([])
  const [detail, setDetail] = useState<ItemStack | null>(null)
  const [useDefId, setUseDefId] = useState<string | null>(null)
  const [equipDefId, setEquipDefId] = useState<string | null>(null)
  const [learnBookDefId, setLearnBookDefId] = useState<string | null>(null)

  const totalItems = inventory.reduce((sum, s) => sum + s.quantity, 0)
  const softCap = getStashSoftCap(hideoutTierId, stashLevel)
  const stackCount = inventory.length
  const capPct = softCap > 0 ? Math.min(100, (stackCount / softCap) * 100) : 0
  const capState =
    stackCount >= softCap ? 'full' : stackCount >= softCap - 5 ? 'warning' : 'ok'

  const visibleStacks = useMemo(() => {
    const effectiveFilter = stashHasAssignedWorker ? filter : 'all'
    const effectiveQuery = stashHasAssignedWorker ? query : ''
    const effectiveSort = stashHasAssignedWorker ? sortBy : 'rarity'
    const lowered = effectiveQuery.trim().toLowerCase()
    return inventory
      .filter((stack) => {
        const def = ITEMS[stack.defId]
        if (!def) return false
        if (effectiveFilter !== 'all' && def.category !== effectiveFilter) return false
        if (lowered.length === 0) return true
        return pick(def.name).toLowerCase().includes(lowered)
      })
      .sort((a, b) => {
        const da = ITEMS[a.defId]
        const db = ITEMS[b.defId]
        if (!da || !db) return 0
        if (effectiveSort === 'quantity') return b.quantity - a.quantity
        if (effectiveSort === 'name') return pick(da.name).localeCompare(pick(db.name))
        const rarity = RARITY_ORDER[da.rarity] - RARITY_ORDER[db.rarity]
        if (rarity !== 0) return rarity
        return pick(da.name).localeCompare(pick(db.name))
      })
  }, [filter, inventory, pick, query, sortBy, stashHasAssignedWorker])

  const grouped = visibleStacks.reduce(
    (acc, stack) => {
      const category = ITEMS[stack.defId]?.category
      if (!category) return acc
      acc[category].push(stack)
      return acc
    },
    {
      food: [] as ItemStack[],
      water: [] as ItemStack[],
      meds: [] as ItemStack[],
      scrap: [] as ItemStack[],
      weapon: [] as ItemStack[],
      trinket: [] as ItemStack[],
    },
  )

  const filterCount = (filterId: 'all' | ItemCategory | 'trinket') => {
    if (filterId === 'all') return inventory.length
    return inventory.filter((stack) => ITEMS[stack.defId]?.category === filterId).length
  }

  const availableSurvivors = useMemo(
    () => survivors.filter((s) => s.status !== 'OnMission'),
    [survivors],
  )
  const selectedUseStack = useMemo(
    () => (useDefId ? inventory.find((stack) => stack.defId === useDefId) ?? null : null),
    [inventory, useDefId],
  )
  const selectedUseDef = selectedUseStack ? ITEMS[selectedUseStack.defId] : null
  const selectedEquipStack = useMemo(
    () => (equipDefId ? inventory.find((stack) => stack.defId === equipDefId) ?? null : null),
    [equipDefId, inventory],
  )
  const selectedEquipDef = selectedEquipStack ? ITEMS[selectedEquipStack.defId] : null
  const selectedEquipWeapon =
    selectedEquipDef && selectedEquipDef.category === 'weapon' && selectedEquipDef.weapon
      ? selectedEquipDef.weapon
      : null
  const selectedLearnStack = useMemo(
    () => (learnBookDefId ? inventory.find((stack) => stack.defId === learnBookDefId) ?? null : null),
    [learnBookDefId, inventory],
  )
  const selectedLearnDef = selectedLearnStack ? ITEMS[selectedLearnStack.defId] : null
  const selectedLearnSkillId = learnBookDefId ? BOOK_SKILL_MAP[learnBookDefId] : null
  const selectedUseConfig =
    selectedUseDef &&
    (selectedUseDef.category === 'food' ||
      selectedUseDef.category === 'water' ||
      selectedUseDef.category === 'meds')
      ? USE_ITEM_CATEGORY_CONFIG[selectedUseDef.category]
      : null
  const rankedUseSurvivors = useMemo(() => {
    if (!selectedUseConfig || !selectedUseDef) return []
    if (selectedUseDef.category === 'meds') {
      return availableSurvivors.filter((s) => s.status === 'Injured')
    }
    const need = selectedUseConfig.need
    return [...availableSurvivors].sort((a, b) => a.needs[need] - b.needs[need])
  }, [availableSurvivors, selectedUseConfig, selectedUseDef])

  const stashRestoreDelta = useMemo(() => {
    if (
      !selectedUseDef ||
      (selectedUseDef.category !== 'food' &&
        selectedUseDef.category !== 'water' &&
        selectedUseDef.category !== 'meds')
    ) {
      return 0
    }
    if (selectedUseDef.category === 'meds') {
      return 0
    }
    return computeStashNeedRestorePreview(
      selectedUseDef.category,
      workstations,
      survivors,
    )
  }, [selectedUseDef, survivors, workstations])

  useEffect(() => {
    if (!selectedUseStack || !selectedUseDef || !selectedUseConfig) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setUseDefId(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selectedUseConfig, selectedUseDef, selectedUseStack])

  useEffect(() => {
    if (!selectedEquipStack || !selectedEquipDef || !selectedEquipWeapon) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setEquipDefId(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selectedEquipDef, selectedEquipStack, selectedEquipWeapon])

  useEffect(() => {
    if (!selectedLearnStack || !selectedLearnSkillId) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLearnBookDefId(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selectedLearnStack, selectedLearnSkillId])

  return (
    <div data-tutorial-id="stash-items" className="space-y-4">
      <div className="grid grid-cols-6 gap-1.5 rounded-lg border border-stone-800 bg-stone-800/70 p-2">
        {MAIN_CATEGORIES.map((cat) => (
          <div
            key={cat}
            className="flex flex-col items-center gap-0.5 rounded-md bg-stone-950/40 py-1.5"
          >
            <span className="text-base leading-none">{CATEGORY_GLYPHS[cat]}</span>
            <span className="font-mono text-sm font-bold text-stone-100">
              {countByCategory(inventory, cat)}
            </span>
            <span className="text-[9px] uppercase tracking-wider text-stone-500">
              {pick(CATEGORY_LABELS[cat])}
            </span>
          </div>
        ))}
        <div className="flex flex-col items-center gap-0.5 rounded-md bg-stone-950/40 py-1.5">
          <span className="text-base leading-none">€</span>
          <span className="font-mono text-sm font-bold text-sky-300">
            {trinketPoints}
          </span>
          <span className="text-[9px] uppercase tracking-wider text-stone-500">
            {t('stash_balance')}
          </span>
        </div>
      </div>

      <div className="rounded-lg border border-stone-800 bg-stone-800/60 px-2 py-2">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-stone-500">
          <span>{t('stash_capacity_label')}</span>
          <span className="font-mono tabular-nums text-stone-400">
            {stackCount}/{softCap}
          </span>
        </div>
        <div className="mt-1.5 h-1.5 overflow-hidden rounded-sm bg-stone-800 ring-1 ring-inset ring-stone-700">
          <div
            className={`h-full transition-[width] duration-300 ${
              capState === 'full'
                ? 'bg-red-600'
                : capState === 'warning'
                  ? 'bg-amber-600'
                  : 'bg-stone-600'
            }`}
            style={{ width: `${capPct}%` }}
          />
        </div>
      </div>

      {stashHasAssignedWorker ? (
        <div className="rounded-lg border border-stone-800 bg-stone-800/60 p-2">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('stash_search_placeholder')}
            className="w-full rounded-md border border-stone-700 bg-stone-950 px-2 py-1.5 text-sm text-stone-100 outline-none placeholder:text-stone-500"
          />
          <div className="mt-2 flex flex-wrap gap-1">
            {FILTERS.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setFilter(option.id)}
                className={`rounded px-2 py-1 text-[10px] uppercase tracking-wider ${
                  filter === option.id
                    ? 'bg-amber-900/50 text-amber-200'
                    : 'bg-stone-800 text-stone-400'
                }`}
              >
                {t(option.labelKey as never)} ({filterCount(option.id)})
              </button>
            ))}
          </div>
          <div className="mt-2 flex gap-1">
            {(['rarity', 'name', 'quantity'] as const).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setSortBy(option)}
                className={`rounded px-2 py-1 text-[10px] uppercase tracking-wider ${
                  sortBy === option
                    ? 'bg-amber-900/50 text-amber-200'
                    : 'bg-stone-800 text-stone-400'
                }`}
              >
                {t(`stash_sort_${option}` as never)}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {capState !== 'ok' && (
        <div
          className={`rounded-md border p-2 text-xs ${
            capState === 'full'
              ? 'border-red-800 bg-red-950/30 text-red-300'
              : 'border-amber-800 bg-amber-950/20 text-amber-300'
          }`}
        >
          {t('stash_capacity_warning', { current: stackCount, cap: softCap })}
        </div>
      )}

      {totalItems === 0 ? (
        <p className="rounded-md border border-stone-800 bg-stone-800/50 p-3 text-xs italic leading-relaxed text-stone-400">
          {t('stash_empty')}
        </p>
      ) : (
        <>
          {[...MAIN_CATEGORIES, 'trinket' as const].map((cat) => {
            const stacks = grouped[cat]
            if (stacks.length === 0) return null
            return (
              <section key={cat}>
                <h2 className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-stone-500">
                  {CATEGORY_GLYPHS[cat]}{' '}
                  {cat === 'trinket' ? t('stash_trinkets') : pick(CATEGORY_LABELS[cat])}
                </h2>
                <div className="space-y-1">
                  {stacks.map((stack) => {
                    const def = ITEMS[stack.defId]
                    if (!def) return null
                    const selectedForDiscard = selected.includes(stack.defId)
                    return (
                      <div
                        key={stack.defId}
                        className="flex items-center justify-between gap-2 rounded-md border border-stone-800 bg-stone-800/50 px-3 py-2"
                      >
                        <div className="min-w-0 flex-1">
                          <button
                            type="button"
                            onClick={() => setDetail(stack)}
                            className={`text-left text-sm font-medium ${RARITY_COLOR[def.rarity]}`}
                          >
                            {pick(def.name)}
                            {def.category === 'trinket' ? (
                              <span className="text-stone-500"> €{def.value}</span>
                            ) : null}{' '}
                            {stack.locked ? '🔒' : ''}
                          </button>
                          <div className="text-[10px] italic text-stone-500">
                            {pick(RARITY_LABELS[def.rarity])}
                          </div>
                        </div>
                        {/* <button
                          type="button"
                          onClick={() => setInventoryLock(stack.defId, !stack.locked)}
                          className="rounded border border-stone-700 px-1.5 py-1 text-[10px] text-stone-400 hover:border-amber-700"
                        >
                          {stack.locked ? t('stash_keep_locked') : t('stash_keep')}
                        </button> */}
                        {(def.category === 'food' ||
                          def.category === 'water' ||
                          def.category === 'meds') && (
                          <button
                            type="button"
                            onClick={() => setUseDefId(stack.defId)}
                            className="rounded border border-emerald-700/80 bg-emerald-950/30 px-1.5 py-1 text-[10px] text-emerald-300 hover:border-emerald-600"
                          >
                            {t('stash_use')}
                          </button>
                        )}
                        {def.category === 'weapon' && (
                          <button
                            type="button"
                            onClick={() => setEquipDefId(stack.defId)}
                            className="rounded border border-amber-700/80 bg-amber-950/30 px-1.5 py-1 text-[10px] text-amber-300 hover:border-amber-600"
                          >
                            {t('detail_weapon_equip')}
                          </button>
                        )}
                        {def.category === 'trinket' && BOOK_SKILL_MAP[stack.defId] && (
                          <button
                            type="button"
                            onClick={() => setLearnBookDefId(stack.defId)}
                            className="rounded border border-purple-700/80 bg-purple-950/30 px-1.5 py-1 text-[10px] text-purple-300 hover:border-purple-600"
                          >
                            {t('stash_book_teach')}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() =>
                            setSelected((prev) =>
                              selectedForDiscard
                                ? prev.filter((id) => id !== stack.defId)
                                : [...prev, stack.defId],
                            )
                          }
                          className={`rounded border px-1.5 py-1 text-[10px] ${
                            selectedForDiscard
                              ? 'border-red-700 text-red-300'
                              : 'border-stone-700 text-stone-400'
                          }`}
                        >
                          {t('stash_drop')}
                        </button>
                        <span className="font-mono text-sm font-bold text-stone-200">
                          ×{stack.quantity}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </section>
            )
          })}

          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => {
                bulkDiscard(selected)
                setSelected([])
              }}
              className="w-full rounded-md border border-red-800/80 bg-red-950/40 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-red-300"
            >
              {t('stash_discard_selected', { count: selected.length })}
            </button>
          )}

          {detail && (() => {
            const def = ITEMS[detail.defId]
            if (!def) return null
            return (
              <div className="fixed inset-x-0 bottom-20 z-30 mx-auto w-[calc(100%-1.5rem)] max-w-[400px] rounded-xl border border-stone-700 bg-stone-950 p-3 shadow-2xl">
                <div className="flex items-start justify-between">
                  <div>
                    <div className={`text-sm font-semibold ${RARITY_COLOR[def.rarity]}`}>
                      {pick(def.name)}
                      {def.category === 'trinket' ? (
                        <span className="text-stone-500"> €{def.value}</span>
                      ) : null}
                    </div>
                    <div className="text-[11px] text-stone-400">{pick(def.blurb)}</div>
                    <div className="mt-1 text-[10px] text-stone-500">
                      {t('stash_detail_rarity')}: {pick(RARITY_LABELS[def.rarity as ItemRarity])} · {t('stash_detail_category')}:{' '}
                      {def.category === 'trinket'
                        ? t('stash_trinkets')
                        : pick(CATEGORY_LABELS[def.category])}{' '}
                      · {t('stash_detail_value')}:{' '}
                      {def.category === 'trinket' ? `€${def.value}` : def.value}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setDetail(null)}
                    className="text-stone-500 hover:text-stone-300"
                  >
                    ✕
                  </button>
                </div>
              </div>
            )
          })()}

          <AnimatePresence>
            {selectedUseStack && selectedUseDef && selectedUseConfig && (
              <motion.div
                key="stash-use-sheet"
                className="fixed inset-0 z-50 flex items-end justify-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
                onClick={() => setUseDefId(null)}
              >
                <div className="absolute inset-0 bg-black/70" />
                <motion.div
                  className="relative w-full max-w-[430px] rounded-t-2xl border-t border-stone-700 bg-stone-950 p-4 pb-6 shadow-2xl"
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  exit={{ y: '100%' }}
                  transition={{ type: 'spring', stiffness: 320, damping: 32 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-stone-700" />
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                    <h2 className="text-base font-semibold text-stone-100">
                      {t(selectedUseConfig.titleKey as never, {
                        item: pick(selectedUseDef.name),
                      })}
                    </h2>
                    {stashRestoreDelta > 0 && (
                      <span
                        className={`font-mono text-base font-semibold ${STASH_DELTA_UI[selectedUseDef.category as 'food' | 'water' | 'meds'].textDelta}`}
                      >
                        +{stashRestoreDelta}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-[11px] text-stone-500">
                    {t(USE_ITEM_CATEGORY_NOTE_KEY[selectedUseDef.category as 'food' | 'water' | 'meds'] as never)}
                  </p>

                  {rankedUseSurvivors.length === 0 ? (
                    <p className="mt-3 rounded-md border border-stone-800 bg-stone-800 p-3 text-sm text-stone-500">
                      {t('ws_sheet_all_away')}
                    </p>
                  ) : (
                    <ul className="mt-3 space-y-2">
                      {rankedUseSurvivors.map((survivor) => {
                        const cat = selectedUseDef.category as 'food' | 'water' | 'meds'
                        const deltaUi = STASH_DELTA_UI[cat]
                        const value = survivor.needs[selectedUseConfig.need]
                        const rounded = Math.round(value)
                        const healWithMeds = cat === 'meds' && survivor.status === 'Injured'
                        const cappedGain = healWithMeds
                          ? 0
                          : Math.min(stashRestoreDelta, Math.max(0, 100 - rounded))
                        const deltaSegmentPct = healWithMeds
                          ? 0
                          : Math.min(stashRestoreDelta, Math.max(0, 100 - rounded))
                        return (
                          <li key={survivor.id}>
                            <button
                              type="button"
                              onClick={() => {
                                applyItemFromStash(selectedUseStack.defId, survivor.id)
                                setUseDefId(null)
                              }}
                              className="w-full rounded-md border border-stone-800 bg-stone-800 p-3 text-left transition-colors hover:border-emerald-700 hover:bg-stone-800"
                            >
                              <div className="flex items-center justify-between gap-2">
                                <span className="font-semibold text-stone-100">
                                  {survivor.name}
                                </span>
                                <span className="shrink-0 font-mono text-xs text-stone-400">
                                  {healWithMeds ? (
                                    <span className="text-red-400">{t('ws_sheet_injured')}</span>
                                  ) : (
                                    <>
                                      {t(NEED_KEYS[selectedUseConfig.need])} {rounded}
                                      <span
                                        className={`ml-1 font-semibold ${
                                          cappedGain === 0 ? 'text-stone-500' : deltaUi.textDelta
                                        }`}
                                      >
                                        +{cappedGain}
                                      </span>
                                    </>
                                  )}
                                </span>
                              </div>
                              {healWithMeds ? (
                                <p className="mt-1 text-[11px] italic text-red-400/90">
                                  {t('ws_sheet_heal_note')}
                                </p>
                              ) : null}
                              <div className="relative mt-1 h-2 overflow-hidden rounded-sm bg-stone-800">
                                <div
                                  className={`absolute inset-y-0 left-0 ${deltaUi.barCurrent}`}
                                  style={{
                                    width: `${Math.max(0, Math.min(100, value))}%`,
                                  }}
                                />
                                <div
                                  className={`absolute inset-y-0 ${deltaUi.barDelta}`}
                                  style={{
                                    left: `${Math.max(0, Math.min(100, value))}%`,
                                    width: `${deltaSegmentPct}%`,
                                  }}
                                />
                              </div>
                            </button>
                          </li>
                        )
                      })}
                    </ul>
                  )}

                  <button
                    type="button"
                    onClick={() => setUseDefId(null)}
                    className="mt-4 w-full rounded-md border border-stone-800 bg-stone-800 px-3 py-2 text-sm uppercase tracking-wider text-stone-400 hover:bg-stone-800"
                  >
                    {t('assign_cancel')}
                  </button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {selectedLearnStack && selectedLearnDef && selectedLearnSkillId && (
              <motion.div
                key="stash-learn-sheet"
                className="fixed inset-0 z-50 flex items-end justify-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
                onClick={() => setLearnBookDefId(null)}
              >
                <div className="absolute inset-0 bg-black/70" />
                <motion.div
                  className="relative w-full max-w-[430px] rounded-t-2xl border-t border-stone-700 bg-stone-950 p-4 pb-6 shadow-2xl"
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  exit={{ y: '100%' }}
                  transition={{ type: 'spring', stiffness: 320, damping: 32 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-stone-700" />
                  <h2 className="text-base font-semibold text-stone-100">
                    {t('stash_book_teach_title', {
                      skill: t(`skill_${selectedLearnSkillId}` as never),
                      item: pick(selectedLearnDef.name),
                    })}
                  </h2>
                  <p className="mt-1 text-[11px] text-stone-500">
                    {t('stash_book_teach_note')}
                  </p>

                  {availableSurvivors.length === 0 ? (
                    <p className="mt-3 rounded-md border border-stone-800 bg-stone-800 p-3 text-sm text-stone-500">
                      {t('ws_sheet_all_away')}
                    </p>
                  ) : (
                    <ul className="mt-3 space-y-2">
                      {availableSurvivors.map((survivor) => {
                        const portraitSrc = getPortraitSrc(survivor.portrait)
                        const initials = survivor.name.slice(0, 2).toUpperCase()
                        const alreadyKnows = isSkillUnlocked(survivor, selectedLearnSkillId)
                        return (
                          <li key={survivor.id}>
                            <button
                              type="button"
                              disabled={alreadyKnows}
                              onClick={() => {
                                learnSkillFromBook(survivor.id, selectedLearnStack.defId)
                                setLearnBookDefId(null)
                              }}
                              className="w-full rounded-md border border-stone-800 bg-stone-800 p-3 text-left transition-colors hover:border-purple-700 hover:bg-stone-800 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <div className="flex items-center gap-2">
                                <div className="h-9 w-9 shrink-0 overflow-hidden rounded-md border border-stone-700 bg-linear-to-br from-stone-800 to-stone-950">
                                  {portraitSrc ? (
                                    <img
                                      src={portraitSrc}
                                      alt={survivor.name}
                                      className="h-full w-full object-cover"
                                    />
                                  ) : (
                                    <div className="grid h-full w-full place-items-center font-mono text-[10px] font-bold text-stone-300">
                                      {initials}
                                    </div>
                                  )}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="truncate font-semibold text-stone-100">
                                    {survivor.name}
                                  </div>
                                  <div className="truncate text-[10px] text-stone-500">
                                    {alreadyKnows
                                      ? t('stash_book_already_known')
                                      : t(`skill_${selectedLearnSkillId}` as never)}
                                  </div>
                                </div>
                              </div>
                            </button>
                          </li>
                        )
                      })}
                    </ul>
                  )}

                  <button
                    type="button"
                    onClick={() => setLearnBookDefId(null)}
                    className="mt-4 w-full rounded-md border border-stone-800 bg-stone-800 px-3 py-2 text-sm uppercase tracking-wider text-stone-400 hover:bg-stone-800"
                  >
                    {t('assign_cancel')}
                  </button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {selectedEquipStack && selectedEquipDef && selectedEquipWeapon && (
              <motion.div
                key="stash-equip-sheet"
                className="fixed inset-0 z-50 flex items-end justify-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
                onClick={() => setEquipDefId(null)}
              >
                <div className="absolute inset-0 bg-black/70" />
                <motion.div
                  className="relative w-full max-w-[430px] rounded-t-2xl border-t border-stone-700 bg-stone-950 p-4 pb-6 shadow-2xl"
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  exit={{ y: '100%' }}
                  transition={{ type: 'spring', stiffness: 320, damping: 32 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-stone-700" />
                  <h2 className="text-base font-semibold text-stone-100">
                    {t('stash_weapon_equip_title', { item: pick(selectedEquipDef.name) })}
                  </h2>
                  <p className="mt-1 text-[11px] text-stone-500">
                    {t('stash_weapon_equip_note')}
                  </p>

                  {availableSurvivors.length === 0 ? (
                    <p className="mt-3 rounded-md border border-stone-800 bg-stone-800 p-3 text-sm text-stone-500">
                      {t('ws_sheet_all_away')}
                    </p>
                  ) : (
                    <ul className="mt-3 space-y-2">
                      {availableSurvivors.map((survivor) => {
                        const portraitSrc = getPortraitSrc(survivor.portrait)
                        const initials = survivor.name.slice(0, 2).toUpperCase()
                        const currentWeapon = survivor.loadout.weapon
                        const currentWeaponLabel = currentWeapon
                          ? pick(currentWeapon.name)
                          : t('equipment_empty_hands')
                        const currentDamage =
                          currentWeapon?.baseDamage && currentWeapon.weaponFamily
                            ? effectiveWeaponDamage(
                                survivor,
                                currentWeapon.baseDamage,
                                currentWeapon.weaponFamily,
                              )
                            : 0
                        const nextDamage = effectiveWeaponDamage(
                          survivor,
                          selectedEquipWeapon.dmg,
                          selectedEquipWeapon.family,
                        )
                        const delta = nextDamage - currentDamage
                        const deltaClass =
                          delta > 0
                            ? 'text-emerald-400'
                            : delta < 0
                              ? 'text-red-400'
                              : 'text-orange-400'
                        const bowLocked =
                          selectedEquipWeapon.family === 'bow' &&
                          !isSkillUnlocked(survivor, 'archery')
                        return (
                          <li key={survivor.id}>
                            <button
                              type="button"
                              disabled={bowLocked}
                              onClick={() => {
                                equipWeaponFromStash(survivor.id, selectedEquipStack.defId)
                                setEquipDefId(null)
                              }}
                              className="w-full rounded-md border border-stone-800 bg-stone-800 p-3 text-left transition-colors hover:border-amber-700 hover:bg-stone-800 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex items-start gap-2">
                                  <div className="h-9 w-9 shrink-0 overflow-hidden rounded-md border border-stone-700 bg-linear-to-br from-stone-800 to-stone-950">
                                    {portraitSrc ? (
                                      <img
                                        src={portraitSrc}
                                        alt={survivor.name}
                                        className="h-full w-full object-cover"
                                      />
                                    ) : (
                                      <div className="grid h-full w-full place-items-center font-mono text-[10px] font-bold text-stone-300">
                                        {initials}
                                      </div>
                                    )}
                                  </div>
                                  <div className="min-w-0">
                                    <div className="truncate font-semibold text-stone-100">
                                      {survivor.name}
                                    </div>
                                    <div className="truncate text-[10px] text-stone-500">
                                      {t('stash_weapon_current')}: {currentWeaponLabel}
                                    </div>
                                  </div>
                                </div>
                                <span className={`shrink-0 font-mono text-xs font-semibold ${deltaClass}`}>
                                  {delta > 0 ? '+' : ''}
                                  {delta}
                                </span>
                              </div>
                              
                              {bowLocked ? (
                                <div className="mt-1 text-[10px] italic text-red-400">
                                  {t('detail_weapon_bow_locked')}
                                </div>
                              ) : null}
                            </button>
                          </li>
                        )
                      })}
                    </ul>
                  )}

                  <button
                    type="button"
                    onClick={() => setEquipDefId(null)}
                    className="mt-4 w-full rounded-md border border-stone-800 bg-stone-800 px-3 py-2 text-sm uppercase tracking-wider text-stone-400 hover:bg-stone-800"
                  >
                    {t('assign_cancel')}
                  </button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

        </>
      )}
    </div>
  )
}
